import * as React from 'react'
import { skipToken, useQuery } from '@tanstack/react-query'
import { Alert, Drawer, Spinner } from '@app/ui'
import { OUTBOUND_EMAIL_CATEGORY_LABELS } from '@lib/enums/outbound-email.enum'
import { isApiError } from '@/helpers/apiClient'
import { formatDateTime } from '@/helpers/formatDate'
import { isPdfFile } from '@/helpers/isPdfFile'
import { toErrorMessage } from '@/helpers/logger'
import { loanKeys } from '@/hooks/queryKeys'
import { PdfActions } from '@/pdf/PdfActions'
import { outboundEmailService } from '@/services/OutboundEmailService'

export type EmailPreviewSource = {
  loanId: string
  emailId: string
  title?: string
}

type EmailPreviewContextValue = {
  open: (source: EmailPreviewSource) => void
  close: () => void
}

const EmailPreviewContext = React.createContext<EmailPreviewContextValue | null>(
  null,
)

function loadErrorMessage(error: Error | null): string | null {
  if (error == null) return null
  return isApiError(error)
    ? (error.body.message ?? 'Could not load email')
    : toErrorMessage(error)
}

/**
 * Right-hand tray for a logged outbound email (body + attachments).
 */
export function EmailPreviewProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [source, setSource] = React.useState<EmailPreviewSource | null>(null)
  const detailQuery = useQuery({
    queryKey: loanKeys.outboundEmail(
      source?.loanId ?? '',
      source?.emailId ?? '',
    ),
    queryFn: source
      ? () => outboundEmailService.get(source.loanId, source.emailId)
      : skipToken,
    // Show a load failure immediately rather than after a retry.
    retry: false,
  })
  const detail = source != null ? (detailQuery.data ?? null) : null
  const loading = source != null && detailQuery.isPending
  const error = source != null ? loadErrorMessage(detailQuery.error) : null

  const close = React.useCallback(() => {
    setSource(null)
  }, [])

  const open = React.useCallback((next: EmailPreviewSource) => {
    setSource(next)
  }, [])

  const value = React.useMemo(
    (): EmailPreviewContextValue => ({ open, close }),
    [open, close],
  )

  const title =
    source?.title ??
    detail?.subject ??
    (source != null ? 'Email' : 'Email')

  return (
    <EmailPreviewContext.Provider value={value}>
      {children}
      <Drawer
        open={source != null}
        onClose={close}
        side="right"
        size="xl"
        padded={false}
        title={title}
      >
        {loading ? (
          <div className="flex h-full items-center justify-center gap-2 text-ds-sm text-ds-muted">
            <Spinner size="sm" label="Loading" />
            Loading email…
          </div>
        ) : null}
        {error ? (
          <div className="p-4">
            <Alert tone="danger" title="Could not load email">
              {error}
            </Alert>
          </div>
        ) : null}
        {detail && !loading && error == null ? (
          <div className="flex h-full min-h-0 flex-col">
            <div className="shrink-0 space-y-1 border-b border-ds-border px-4 py-3 text-ds-sm">
              <div>
                <span className="text-ds-muted">To </span>
                {detail.recipients
                  .map((r) =>
                    r.name ? `${r.name} <${r.address}>` : r.address,
                  )
                  .join(', ')}
              </div>
              <div>
                <span className="text-ds-muted">From </span>
                {detail.fromAddress}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-ds-xs text-ds-muted">
                <span>
                  {OUTBOUND_EMAIL_CATEGORY_LABELS[detail.category]}
                </span>
                <span>{formatDateTime(detail.sentAt)}</span>
                {detail.sentByName ? <span>{detail.sentByName}</span> : null}
                {detail.status === 'CONSOLE' ? (
                  <span>Logged (console)</span>
                ) : null}
                {detail.status === 'FAILED' ? (
                  <span className="text-ds-danger">Failed</span>
                ) : null}
              </div>
              {detail.error ? (
                <Alert tone="danger" title="Send error">
                  {detail.error}
                </Alert>
              ) : null}
              {detail.attachments.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {detail.attachments.map((att) => (
                    <li
                      key={att.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="min-w-0 truncate text-ds-xs text-ds-text">
                        {att.fileName}
                      </span>
                      {source && isPdfFile(att.fileName, att.contentType) ? (
                        <PdfActions
                          title={att.fileName}
                          fileName={att.fileName}
                          load={() =>
                            outboundEmailService.downloadAttachment(
                              source.loanId,
                              detail.id,
                              att.id,
                            )
                          }
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <iframe
              aria-label={detail.subject}
              sandbox=""
              srcDoc={detail.html}
              className="min-h-0 w-full flex-1 border-0 bg-white"
            />
          </div>
        ) : null}
      </Drawer>
    </EmailPreviewContext.Provider>
  )
}

export function useEmailPreview(): EmailPreviewContextValue {
  const ctx = React.useContext(EmailPreviewContext)
  if (ctx == null) {
    throw new Error('useEmailPreview must be used within EmailPreviewProvider')
  }
  return ctx
}
